import React, { useEffect, useState } from "react";
import axios from "axios";
import Modal from "react-modal"; // Install: npm install react-modal

Modal.setAppElement("#root"); // Important for accessibility

const DriverList = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const driversPerPage = 5;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editedDriver, setEditedDriver] = useState(null);

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = () => {
    axios
      .get(`${process.env.REACT_APP_BACKEND}/api/driver`)
      .then((response) => {
        setDrivers(response.data);
        setLoading(false);
      })
      .catch((error) => {
        setError("Failed to fetch drivers");
        setLoading(false);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this driver?")) {
      axios
        .delete(`${process.env.REACT_APP_BACKEND}/api/driver/${id}`)
        .then(() => {
          setDrivers(drivers.filter((driver) => driver._id !== id));
        })
        .catch((error) => {
          alert("Failed to delete driver");
        });
    }
  };

  const openModal = (driver) => {
    setSelectedDriver(driver);
    setEditedDriver({ ...driver });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDriver(null);
    setEditedDriver(null);
  };

  const handleEditChange = (e) => {
    setEditedDriver({ ...editedDriver, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    axios
      .put(`${process.env.REACT_APP_BACKEND}/api/driver/${selectedDriver._id}`, editedDriver)
      .then(() => {
        setDrivers(
          drivers.map((driver) =>
            driver._id === selectedDriver._id ? editedDriver : driver
          )
        );
        closeModal();
      })
      .catch((error) => {
        alert("Failed to edit driver");
      });
  };

  const filteredDrivers = drivers.filter((driver) => {
    const fullName = `${driver.firstname} ${driver.lastname}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const indexOfLastDriver = currentPage * driversPerPage;
  const indexOfFirstDriver = indexOfLastDriver - driversPerPage;
  const currentDrivers = filteredDrivers.slice(indexOfFirstDriver, indexOfLastDriver);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredDrivers.length / driversPerPage); i++) {
    pageNumbers.push(i);
  }

  if (loading) return <p className="text-center mt-4">Loading driver list...</p>;
  if (error) return <p className="text-danger text-center">{error}</p>;

  return (
    <div className="driver-list-container container mt-4">
      <h2 className="driver-list-title text-center mb-4">Driver List</h2>

      <div className="mb-3">
        <input
          type="text"
          className="driver-search-input form-control"
          placeholder="Search drivers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table className="driver-table table table-striped table-bordered table-hover">
          <thead className="driver-table-head">
            <tr>
              <th>#</th>
              <th>Firstname</th>
              <th>Lastname</th>
              <th>Dob</th>
              <th>Gender</th>
              <th>Area</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentDrivers.map((driver, index) => (
              <tr key={driver._id}>
                <td>{indexOfFirstDriver + index + 1}</td>
                <td>{driver.firstname}</td>
                <td>{driver.lastname}</td>
                <td>{driver.dob}</td>
                <td>{driver.gender}</td>
                <td>{driver.area}</td>
                <td>
                  <img
                    src={`${process.env.REACT_APP_BACKEND}${driver.image}`}
                    alt="Driver"
                    className="driver-image img-fluid rounded"
                    onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/80x80?text=No+Image'; }}
                    style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'cover' }}
                  />
                </td>
                <td>
                  <button className="btn btn-danger btn-sm me-1" onClick={() => handleDelete(driver._id)}>
                    Delete
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => openModal(driver)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <nav>
        <ul className="pagination justify-content-center">
          {pageNumbers.map((number) => (
            <li key={number} className={`page-item ${currentPage === number ? "active" : ""}`}>
              <button onClick={() => paginate(number)} className="page-link">
                {number}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <Modal isOpen={isModalOpen} onRequestClose={closeModal} contentLabel="Edit Driver" className="edit-driver-modal" overlayClassName="edit-driver-modal-overlay">
        <div className="edit-driver-modal-content">
          <div className="edit-driver-modal-header">
            <h3>Edit Driver</h3>
            <button className="edit-driver-modal-close" onClick={closeModal}>&times;</button>
          </div>
          {editedDriver && (
            <form className="edit-driver-form" onSubmit={handleEditSubmit}>
              <div className="edit-driver-form-group">
                <label htmlFor="firstname">Firstname</label>
                <input type="text" id="firstname" name="firstname" value={editedDriver.firstname} onChange={handleEditChange} placeholder="Firstname" required />
              </div>
              <div className="edit-driver-form-group">
                <label htmlFor="lastname">Lastname</label>
                <input type="text" id="lastname" name="lastname" value={editedDriver.lastname} onChange={handleEditChange} placeholder="Lastname" required />
              </div>
              <div className="edit-driver-form-group">
                <label htmlFor="dob">Date of Birth</label>
                <input type="text" id="dob" name="dob" value={editedDriver.dob} onChange={handleEditChange} placeholder="Date of Birth" required />
              </div>
              <div className="edit-driver-form-group">
                <label htmlFor="gender">Gender</label>
                <input type="text" id="gender" name="gender" value={editedDriver.gender} onChange={handleEditChange} placeholder="Gender" required />
              </div>
              <div className="edit-driver-form-group">
                <label htmlFor="area">Area</label>
                <input type="text" id="area" name="area" value={editedDriver.area} onChange={handleEditChange} placeholder="Area" required />
              </div>
              <div className="edit-driver-modal-actions">
                <button type="submit" className="edit-driver-save-btn">Save</button>
                <button type="button" className="edit-driver-cancel-btn" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DriverList;
